import Intercom, { shutdown } from '@intercom/messenger-js-sdk';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';

import type { GetAuthMe200User } from '@/api/generated/lucioAPI.schemas';
import { getAccessToken } from '@/config/auth/deviceToken';
import { decodeJwtPayload } from '@/config/auth/jwt';
import { queryKeys } from '@/config/query/queryKeys';
import { useIntercomToken } from '@/features/auth/hooks/useIntercomToken';
import { type Deployment, getStoredDeployment } from '@/utils/apiBaseUrl';
import { useIsAuthenticated } from '@/utils/stores/useAuthStore';

interface IntercomProviderProps {
  children: ReactNode;
}

const INTERCOM_APP_ID: Partial<Record<Deployment, string>> & { default: string } = {
  default: 'm12ccdxu',
  trilegal: 'ony3sjm2',
};

interface IntercomCompany {
  company_id: string;
  name: string;
}

// The org identity now comes from the Clerk session (organizationMetadata),
// not a client-side picker. Fall back to the deployment as a coarse company
// grouping when no org id is available.
function getIntercomCompany(
  orgId: string | null,
  deployment: Deployment | null,
): IntercomCompany | undefined {
  if (orgId) {
    return { company_id: orgId, name: orgId };
  }
  if (deployment) {
    return { company_id: deployment, name: deployment.toUpperCase() };
  }
  return undefined;
}

/** Reads the Clerk org id from the current access token's claims (best effort). */
function useClerkOrgId(enabled: boolean): string | null {
  const [orgId, setOrgId] = useState<string | null>(null);

  useEffect(() => {
    // A stale orgId while disabled is harmless — the company is only read
    // when `ready` (which implies enabled) is true.
    if (!enabled) {
      return;
    }
    let cancelled = false;
    void getAccessToken().then((token) => {
      if (cancelled || !token) {
        return;
      }
      const claims = decodeJwtPayload<{
        org_id?: string;
        organizationMetadata?: { organizationId?: string };
      }>(token);
      setOrgId(claims?.organizationMetadata?.organizationId ?? claims?.org_id ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return orgId;
}

export function IntercomProvider({ children }: IntercomProviderProps) {
  const { isAuthenticated } = useIsAuthenticated();
  const deployment = getStoredDeployment();
  const appId = (deployment ? INTERCOM_APP_ID[deployment] : undefined) ?? INTERCOM_APP_ID.default;

  const { data: user } = useQuery<GetAuthMe200User>({
    enabled: false,
    queryKey: queryKeys.auth.user(),
  });

  const ready = Boolean(isAuthenticated && user?.id && appId);
  const { data: token } = useIntercomToken(ready);
  const orgId = useClerkOrgId(ready);

  useEffect(() => {
    if (!ready || !token || !appId || !user) {
      return;
    }

    const company = getIntercomCompany(orgId, deployment);

    Intercom({
      app_id: appId,
      email: user.email,
      hide_default_launcher: true,
      intercom_user_jwt: token,
      name: user.displayName ?? user.email,
      user_id: user.clerkId ?? user.id,
      ...(company ? { company } : {}),
    });
  }, [ready, token, appId, user, deployment, orgId]);

  useEffect(() => {
    if (isAuthenticated) {
      return;
    }
    shutdown();
  }, [isAuthenticated]);

  useEffect(() => {
    return () => {
      shutdown();
    };
  }, []);

  return <>{children}</>;
}
